import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const API_URL = process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com';

// Render Strapi 5 "blocks" instructions as plain numbered steps
function Instructions({ blocks }: { blocks: any }) {
  if (!blocks || !Array.isArray(blocks) || blocks.length === 0) {
    return <p className="text-sm text-gray-400">No instructions.</p>;
  }
  const inline = (children: any[]) => (children || []).map((c) => c?.text || '').join('');
  return (
    <ol className="space-y-2 list-decimal pl-6">
      {blocks.map((b: any, i: number) => {
        if (b.type === 'list' && Array.isArray(b.children)) {
          return b.children.map((li: any, j: number) => (
            <li key={`${i}-${j}`} className="text-gray-700">{inline(li.children)}</li>
          ));
        }
        if (b.type === 'heading') {
          return <li key={i} className="text-gray-900 font-semibold">{inline(b.children)}</li>;
        }
        return <li key={i} className="text-gray-700">{inline(b.children || [])}</li>;
      })}
    </ol>
  );
}

function resolveImage(img: any): string | null {
  if (!img) return null;
  const url = img.url || img.formats?.large?.url || img.formats?.medium?.url || img.formats?.small?.url;
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
}

export default function RecipePreviewPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const [params] = useSearchParams();
  const secret = params.get('secret') || '';
  const status = (params.get('status') === 'draft' ? 'draft' : 'published') as 'draft' | 'published';

  const [recipe, setRecipe] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId || !secret) {
      setError('Missing preview parameters.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    axios
      .get(`${API_URL}/api/recipes/preview/${documentId}`, {
        params: { secret, status },
      })
      .then((res) => setRecipe(res.data?.data || null))
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load preview'))
      .finally(() => setLoading(false));
  }, [documentId, secret, status]);

  const imgUrl = recipe ? resolveImage(recipe.image) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Recipe preview — {recipe?.title || documentId}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Status banner */}
      <div className={`w-full px-4 py-2 text-center text-xs font-semibold ${
        status === 'draft'
          ? 'bg-amber-100 text-amber-800 border-b border-amber-200'
          : 'bg-green-100 text-green-800 border-b border-green-200'
      }`}>
        {status === 'draft' ? '⚠ Preview · DRAFT (unpublished)' : '✓ Preview · Published version'}
      </div>

      {loading ? (
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="h-64 bg-gray-200 rounded-3xl animate-pulse mb-4" />
          <div className="h-8 bg-gray-200 rounded animate-pulse mb-3 w-2/3" />
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded animate-pulse" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-5/6" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-4/6" />
          </div>
        </div>
      ) : error ? (
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="text-4xl mb-3">⚠️</div>
          <h1 className="font-bold text-gray-900 text-lg mb-2">Preview unavailable</h1>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      ) : recipe ? (
        <div className="max-w-3xl mx-auto px-4 py-6">
          {/* Hero image */}
          <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-orange-200 via-red-300 to-red-500 aspect-[16/9] mb-5 shadow-lg">
            {imgUrl ? (
              <img src={imgUrl} alt={recipe.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-8xl opacity-30">🍖</div>
            )}
          </div>

          {/* Title + category */}
          <div className="mb-5">
            {recipe.recipe_category?.recipe_category_name && (
              <div className="inline-block bg-red-50 text-red-700 rounded-full px-2.5 py-0.5 text-[11px] font-bold mb-2">
                {recipe.recipe_category.recipe_category_name}
              </div>
            )}
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight">{recipe.title}</h1>
          </div>

          {/* Meta strip */}
          <div className="flex flex-wrap gap-2 mb-5 text-xs">
            {recipe.cooking_time && (
              <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full px-3 py-1 font-bold">⏱ {recipe.cooking_time}</span>
            )}
            {(recipe.servings || 0) > 0 && (
              <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 rounded-full px-3 py-1 font-bold">🍽 {recipe.servings} servings</span>
            )}
            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1 font-bold">👍 {recipe.thumbs_up_count || 0}</span>
            <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 rounded-full px-3 py-1 font-bold">👎 {recipe.thumbs_down_count || 0}</span>
          </div>

          {/* Description */}
          {recipe.description && (
            <p className="text-gray-700 leading-relaxed mb-6 whitespace-pre-wrap">{recipe.description}</p>
          )}

          {/* Nutrients */}
          {recipe.nutrients?.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Nutrition per serving</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {recipe.nutrients.map((n: any, i: number) => (
                  <div key={i} className="bg-white border border-gray-100 rounded-xl p-3 text-center">
                    <div className="text-lg font-extrabold text-gray-900">{n.value || '—'}</div>
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-0.5">{n.nutrient_name || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ingredients */}
          {recipe.ingredients?.length > 0 && (
            <div className="mb-6">
              <h2 className="font-bold text-gray-900 mb-2 inline-flex items-center gap-2">🥩 Ingredients · {recipe.ingredients.length}</h2>
              <ul className="space-y-3 bg-white border border-gray-100 rounded-2xl p-4">
                {recipe.ingredients.map((ing: any, i: number) => {
                  const variants = Array.isArray(ing.replaceableIngredients) ? ing.replaceableIngredients : [];
                  return (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-gray-300 mt-1">•</span>
                      <div className="text-sm text-gray-800 flex-1 min-w-0">
                        <div>
                          <span className="font-semibold">{ing.IngredientName || '(unnamed)'}</span>
                          {(ing.Quantity || ing.Unit) && (
                            <span className="text-gray-500"> · {ing.Quantity} {ing.Unit}</span>
                          )}
                          {ing.Comments && <span className="italic text-gray-500 ml-1">({ing.Comments})</span>}
                          {variants.length > 0 && (
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                              replaceable
                            </span>
                          )}
                        </div>
                        {variants.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold pt-1">Swap with:</span>
                            {variants.map((v: any, vi: number) => {
                              const repName = v?.IngredientName;
                              const label = typeof repName === 'object'
                                ? (repName?.ingredient_name || repName?.name || '—')
                                : (repName || '—');
                              return (
                                <span key={vi} className="text-[11px] bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 font-medium">
                                  {label}
                                  {v?.Comment && <span className="text-gray-400 ml-1">· {v.Comment}</span>}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Instructions */}
          {recipe.instructions && (
            <div className="mb-6">
              <h2 className="font-bold text-gray-900 mb-2 inline-flex items-center gap-2">📝 Instructions</h2>
              <div className="bg-white border border-gray-100 rounded-2xl p-4">
                <Instructions blocks={recipe.instructions} />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-[11px] text-gray-400 py-4 mt-4 border-t border-gray-200">
            Preview · documentId <code>{documentId}</code> · status <strong className="uppercase">{status}</strong>
          </div>
        </div>
      ) : null}
    </div>
  );
}
